import { getJobStatus, JobStreamUpdate, JobStatus } from "./workProcessor";

/**
 * Callback types for job status updates
 */
export interface JobCallbacks {
    onUpdate: (data: JobStreamUpdate) => void;
    onComplete: (result: JobStreamUpdate) => void;
    onError: (error: Error) => void;
}

/**
 * Internal job registration entry
 */
interface RegisteredJob {
    jobId: string;
    callbacks: JobCallbacks;
    registeredAt: number;
}

/**
 * Batch status response from the API
 */
interface BatchStatusResponse {
    jobs: { [jobId: string]: JobStatus };
    not_found: string[];
}

/**
 * Centralized Job Status Manager
 *
 * This singleton manages all job status polling through a single timer,
 * making batch requests to reduce API calls when multiple jobs are running.
 *
 * Usage:
 *   const cleanup = JobStatusManager.getInstance().registerJob(jobId, token, callbacks);
 *   // Later: cleanup() to unregister
 */
class JobStatusManager {
    private static instance: JobStatusManager;

    private registeredJobs: Map<string, RegisteredJob> = new Map();
    private pollInterval: ReturnType<typeof setInterval> | null = null;
    private currentToken: string | null = null;
    private isPolling: boolean = false;

    // Polling configuration
    private readonly POLL_INTERVAL_MS = 5000; // 5 seconds (matches backend recommendation)

    private constructor() {
        // Private constructor for singleton
    }

    /**
     * Get the singleton instance
     */
    public static getInstance(): JobStatusManager {
        if (!JobStatusManager.instance) {
            JobStatusManager.instance = new JobStatusManager();
        }
        return JobStatusManager.instance;
    }

    /**
     * Register a job for status polling
     *
     * @param jobId - The job ID to track
     * @param token - Auth token for API calls
     * @param callbacks - Callbacks for updates, completion, and errors
     * @returns Cleanup function to unregister the job
     */
    public registerJob(
        jobId: string,
        token: string,
        callbacks: JobCallbacks
    ): () => void {
        console.log(`[JobStatusManager] Registering job: ${jobId}`);

        // Store the token (will be updated with each registration)
        this.currentToken = token;

        // Register the job
        this.registeredJobs.set(jobId, {
            jobId,
            callbacks,
            registeredAt: Date.now()
        });

        // Start polling if not already running
        this.startPolling();

        // Return cleanup function
        return () => this.unregisterJob(jobId);
    }

    /**
     * Unregister a job from status polling
     */
    public unregisterJob(jobId: string): void {
        console.log(`[JobStatusManager] Unregistering job: ${jobId}`);

        this.registeredJobs.delete(jobId);

        // Stop polling if no more jobs
        if (this.registeredJobs.size === 0) {
            this.stopPolling();
        }
    }

    /**
     * Get the count of currently registered jobs
     */
    public getActiveJobCount(): number {
        return this.registeredJobs.size;
    }

    /**
     * Get all registered job IDs
     */
    public getRegisteredJobIds(): string[] {
        return Array.from(this.registeredJobs.keys());
    }

    /**
     * Start the polling interval
     */
    private startPolling(): void {
        if (this.pollInterval !== null) {
            // Already polling
            return;
        }

        console.log(`[JobStatusManager] Starting polling with ${this.registeredJobs.size} job(s)`);

        // Poll immediately
        this.pollJobStatuses();

        // Then poll at intervals
        this.pollInterval = setInterval(() => {
            this.pollJobStatuses();
        }, this.POLL_INTERVAL_MS);
    }

    /**
     * Stop the polling interval
     */
    private stopPolling(): void {
        if (this.pollInterval !== null) {
            console.log(`[JobStatusManager] Stopping polling`);
            clearInterval(this.pollInterval);
            this.pollInterval = null;
        }
    }

    /**
     * Poll for all registered job statuses using batch endpoint
     */
    private async pollJobStatuses(): Promise<void> {
        if (this.isPolling) {
            console.log(`[JobStatusManager] Skipping poll - already in progress`);
            return;
        }

        if (this.registeredJobs.size === 0) {
            return;
        }

        if (!this.currentToken) {
            console.error(`[JobStatusManager] No auth token available`);
            return;
        }

        this.isPolling = true;
        const jobIds = Array.from(this.registeredJobs.keys());

        console.log(`[JobStatusManager] Polling ${jobIds.length} job(s):`, jobIds);

        try {
            // Each job is resolved to its Product and authorized by
            // Intelligenic before the processor is queried. A direct batch
            // processor call cannot safely represent mixed-Product jobs.
            const statuses = await Promise.all(jobIds.map(async jobId => {
                try {
                    return [jobId, await getJobStatus(jobId, this.currentToken!)] as const;
                } catch {
                    return [jobId, null] as const;
                }
            }));
            const jobs = Object.fromEntries(
                statuses.filter((entry): entry is readonly [string, JobStatus] => entry[1] !== null)
            );
            const batchResponse: BatchStatusResponse = {
                jobs,
                not_found: statuses.filter(([, status]) => status === null).map(([jobId]) => jobId)
            };
            console.log(`[JobStatusManager] Batch response:`, batchResponse);

            // Process each job's status (jobs are nested under 'jobs' key)
            this.processJobStatuses(batchResponse.jobs || {});

        } catch (error) {
            console.error(`[JobStatusManager] Error polling job statuses:`, error);

            // Notify all registered jobs of the error
            // But don't unregister them - let the next poll try again
            // Only fail individual jobs if they explicitly error
        } finally {
            this.isPolling = false;
        }
    }

    /**
     * Process batch status response and distribute to callbacks
     */
    private processJobStatuses(jobStatuses: { [jobId: string]: JobStatus }): void {
        for (const [jobId, status] of Object.entries(jobStatuses)) {
            const registeredJob = this.registeredJobs.get(jobId);

            if (!registeredJob) {
                // Job was unregistered while polling
                continue;
            }

            const { callbacks } = registeredJob;

            // Convert to JobStreamUpdate format
            const update: JobStreamUpdate = {
                job_id: status.jobid || jobId,
                status: status.status,
                progress: status.progress,
                message: status.message,
                timestamp: Date.now(),
                result: (status as any).result,
                output: (status as any).output,
                current_section: (status as any).current_section,
                total_sections: (status as any).total_sections
            };

            try {
                // Check for different status types
                if (status.status === "COMPLETED") {
                    console.log(`[JobStatusManager] Job ${jobId} completed`);
                    callbacks.onComplete(update);
                    this.unregisterJob(jobId);
                } else if (status.status === "FAILED" || status.status === "ERROR") {
                    console.log(`[JobStatusManager] Job ${jobId} failed:`, status.message);
                    callbacks.onError(new Error(status.message || "Job failed"));
                    this.unregisterJob(jobId);
                } else if (
                    status.status === "PROCESSING" ||
                    status.status === "ACCEPTED" ||
                    status.status === "PENDING"
                ) {
                    // Progress update
                    callbacks.onUpdate(update);
                }
            } catch (callbackError) {
                console.error(`[JobStatusManager] Error in callback for job ${jobId}:`, callbackError);
            }
        }

        // Check for jobs that weren't in the response (might be 404/not found)
        for (const jobId of this.registeredJobs.keys()) {
            if (!(jobId in jobStatuses)) {
                console.warn(`[JobStatusManager] Job ${jobId} not found in batch response`);
                // Don't immediately fail - the job might not be created yet
                // Let it retry on the next poll
            }
        }
    }

    /**
     * Force a poll immediately (useful after registering a job)
     */
    public forcePoll(): void {
        if (this.registeredJobs.size > 0 && !this.isPolling) {
            this.pollJobStatuses();
        }
    }

    /**
     * Clear all registered jobs and stop polling
     * (useful for cleanup on logout/unmount)
     */
    public clearAll(): void {
        console.log(`[JobStatusManager] Clearing all ${this.registeredJobs.size} registered jobs`);
        this.registeredJobs.clear();
        this.stopPolling();
        this.currentToken = null;
    }
}

// Export singleton instance getter
export const getJobStatusManager = () => JobStatusManager.getInstance();

// Export the class for testing purposes
export { JobStatusManager };
